import MapboxDraw, { DrawCustomMode, DrawCustomModeThis } from "@mapbox/mapbox-gl-draw";
import { Feature } from "geojson";

import { captureBeforeMoveFeatures, initializeValidation, validateAfterMove, ValidationState } from "fond/draw/validation/helper";
import { clickEventBBox } from "fond/utils/mapbox";

import CustomSnapDirectSelect from "./snap/CustomSnapDirectSelect";
import { snapWithPointPriority } from "./snap/helper";
import {
  ColocatedFeature,
  extendLineString,
  filterEditableFeatures,
  findColocatedFeaturesAtLocation,
  getFeatureTarget,
  getVertexCoordinates,
  isClickOnDrawFeature,
  isLineStringEndVertex,
  moveColocatedFeatures,
  removeGuides,
} from "./helper";

const {
  modes,
  constants: Constants,
  lib: { createSupplementaryPoints },
} = MapboxDraw;
interface DirectSelectState extends ValidationState {
  canDragMove?: boolean;
  midPoints: boolean;
  featureId?: string;
  selectedCoordPaths?: string[];
  colocatedFeatures?: ColocatedFeature[] | null;
  dragMoveLocation?: { lng: number; lat: number };
  dragMoving?: boolean;
  skipNextClick?: boolean; // Flag to skip the next onClick after transitioning from vertex click
  snapList?: mapboxgl.MapboxGeoJSONFeature[] | null;
  vertices?: Array<[number, number]> | null;
}

interface DrawCustomModeExtension extends DrawCustomMode<DirectSelectState> {
  onDrag(this: DrawCustomModeThis, state: DirectSelectState, event: MapboxDraw.MapMouseEvent): void;
}

const directSelect: DrawCustomModeExtension = {
  ...CustomSnapDirectSelect,

  onSetup: function (opts) {
    const state = CustomSnapDirectSelect.onSetup?.call(this, opts);
    state.colocatedFeatures = null;
    state.midPoints = this._ctx.options.midPoints !== false; // default to true if not set

    // Initialize validation
    initializeValidation.call(this, state);

    removeGuides.call(this);

    // Check if we're transitioning from simple_select with a vertex click. We want to allow
    // the user to immediately drag the vertex without having to click again, so we set up the state as if a mousedown just happened on that vertex.
    if (opts.startPos && opts.coordPath) {
      // Flag to skip the next onClick event (which will fire from the original click)
      // to prevent the feature from being deselected
      state.skipNextClick = true;

      // Set the selected coordinate path so the vertex is already selected
      state.selectedCoordPaths = [opts.coordPath];

      // Properly initialize the selection by calling setSelectedCoordinates
      // This matches what onVertex does in the base class
      const selectedCoordinates = this.pathsToCoordinates(state.featureId, state.selectedCoordPaths);
      this.setSelectedCoordinates(selectedCoordinates);

      // Initialize drag state as if mousedown just happened
      state.dragMoveLocation = opts.startPos;
      state.dragMoving = false; // Will become true on first mousemove

      // Important: Disable map dragging immediately since we're about to drag a vertex
      if (this.map.dragPan.isEnabled()) {
        this.map.dragPan.disable();
      }

      // Trigger a render to show the selected vertex
      this.setActionableState({
        combineFeatures: false,
        uncombineFeatures: false,
        trash: true,
      });

      // Manually set up validation state without calling onMouseDown
      // (avoids interfering with real event flow)
      if (state.featureId) {
        const feature = this.getFeature(state.featureId);
        if (feature) {
          captureBeforeMoveFeatures.call(this, state, [feature.toGeoJSON() as Feature]);
        }
      }

      // Simulate a mousedown event to initialize drag state
      // This allows CustomSnapDirectSelect.onDrag to work properly
      const syntheticEvent = {
        featureTarget: {
          properties: {
            meta: Constants.meta.VERTEX,
            parent: opts.featureId,
            coord_path: opts.coordPath,
          },
        },
        lngLat: opts.startPos,
        point: this.map.project([opts.startPos.lng, opts.startPos.lat]),
        originalEvent: {},
      } as unknown as MapboxDraw.MapMouseEvent;

      // Call onMouseDown to set up drag state with capture and colocation
      this.onMouseDown(state, syntheticEvent);
    }

    return state;
  },

  onMouseMove: function (state, event) {
    // Check if left mouse button is pressed (buttons === 1 means left button down)
    const isMouseDown = event.originalEvent?.buttons === 1;

    // If we have a pre-selected vertex from simple_select with dragMoveLocation set,
    // and the mouse button is still pressed, continue dragging
    if (state.dragMoveLocation && state.selectedCoordPaths?.length === 1 && isMouseDown) {
      // Mark that we've started dragging
      state.dragMoving = true;
      this.onDrag(state, event);
      // Important: return true to skip render, matching base behavior
      return true;
    }

    // If button is released, clear the drag state
    if (state.dragMoveLocation && !isMouseDown) {
      state.dragMoveLocation = undefined;
      state.dragMoving = false;
    }

    // Call base onMouseMove first
    const result = CustomSnapDirectSelect.onMouseMove?.call(this, state, event);

    // Handle cursor change for ALT+vertex extend mode
    // Override cursor after base call to provide visual feedback
    if (state.midPoints && event.originalEvent?.altKey && state.selectedCoordPaths?.length === 1 && state.featureId) {
      const feature = this.getFeature(state.featureId);

      if (feature && isLineStringEndVertex(feature, state.selectedCoordPaths[0])) {
        // Set ADD cursor (crosshair) to indicate extend mode is active
        this.updateUIClasses({ mouse: Constants.cursors.ADD });
      }
    } else if (state.selectedCoordPaths?.length === 1 && state.featureId) {
      // ALT is not pressed but we have a vertex selected
      // Reset cursor to MOVE (default for vertex selection)
      this.updateUIClasses({ mouse: Constants.cursors.MOVE });
    }

    return result;
  },

  onMouseDown: function (state, event) {
    // @ts-expect-error featureTarget is possibly undefined & needs to allow for that
    event.featureTarget = getFeatureTarget.call(this, event, this._ctx.options);

    // Check if ALT is pressed with a selected vertex on a LineString
    // Allow extending the line by clicking on the map (only if midPoints are enabled)
    if (state.midPoints && event.originalEvent?.altKey && state.selectedCoordPaths?.length === 1 && state.featureId) {
      const feature = this.getFeature(state.featureId);

      if (feature && isLineStringEndVertex(feature, state.selectedCoordPaths[0])) {
        // Check if clicking on empty map (not on another feature/vertex)
        if (!isClickOnDrawFeature(event)) {
          const coordPath = state.selectedCoordPaths[0];
          const newCoords: [number, number] = [event.lngLat.lng, event.lngLat.lat];
          const newIndex = extendLineString(feature, coordPath, newCoords);

          if (newIndex !== null) {
            // Update selected vertex to the new end vertex so user can continue extending
            state.selectedCoordPaths = [newIndex.toString()];

            // Update the selected coordinates for rendering
            const selectedCoordinates = this.pathsToCoordinates(state.featureId, state.selectedCoordPaths);
            this.setSelectedCoordinates(selectedCoordinates);

            // Skip the next onClick to prevent deselection
            state.skipNextClick = true;

            // Trigger re-render
            this.setActionableState({
              combineFeatures: false,
              uncombineFeatures: false,
              trash: true,
            });

            // @ts-expect-error - fireUpdate exists at runtime but not in types
            this.fireUpdate();

            return; // Don't process normal mousedown
          }
        }
      }
    }

    // Check if the feature can be edited before allowing any dragging
    if (event.featureTarget?.properties) {
      // For vertices and midpoints, use the parent feature ID
      const featureId =
        event.featureTarget.properties.meta === Constants.meta.VERTEX || event.featureTarget.properties.meta === Constants.meta.MIDPOINT
          ? event.featureTarget.properties.parent
          : event.featureTarget.properties.id;

      if (featureId) {
        const feature = this.getFeature(featureId);
        if (feature) {
          const editableFeatures = filterEditableFeatures(this._ctx.options, [feature.toGeoJSON() as Feature]);
          if (editableFeatures.length === 0) {
            // Feature is not editable, don't allow dragging vertices or the entire feature
            return;
          }
        }
      }
    }

    // Call base onMouseDown first to set selectedCoordPaths
    modes.direct_select.onMouseDown?.call(this, state, event);

    // Capture features for validation before vertex move
    if (state.featureId) {
      const feature = this.getFeature(state.featureId);
      if (feature) {
        captureBeforeMoveFeatures.call(this, state, [feature.toGeoJSON() as Feature]);
      }
    }

    // Find colocated features if topologicalEditing is enabled and we have a selected vertex
    if (this._ctx.options.topologicalEditing && state.featureId && state.selectedCoordPaths && state.selectedCoordPaths.length === 1) {
      const feature = this.getFeature(state.featureId);
      if (feature) {
        const coordPath = state.selectedCoordPaths[0];
        const parts = coordPath.split(".");
        const coords = feature.getCoordinates();

        let vertexCoords: [number, number] | null = null;
        if (feature.type === "Point") {
          vertexCoords = coords as [number, number];
        } else if (feature.type === "LineString") {
          const index = parseInt(parts[0], 10);
          vertexCoords = (coords as Array<[number, number]>)[index];
        } else if (feature.type === "Polygon") {
          const ringIndex = parseInt(parts[0], 10);
          const coordIndex = parseInt(parts[1], 10);
          vertexCoords = (coords as Array<Array<[number, number]>>)[ringIndex][coordIndex];
        }

        if (vertexCoords) {
          // Create a bounding box around the actual vertex coordinates
          const point = this.map.project(vertexCoords);
          const bbox = clickEventBBox({ point } as mapboxgl.MapMouseEvent);

          // Query features at this location
          const nearbyFeatures = this.featuresAt(null, bbox, "click");

          // Find colocated features at this location, filtered by editingLayerIds
          const allowedLayerIds = this._ctx.options.editOptions?.editingLayerIds;
          const colocated = findColocatedFeaturesAtLocation(vertexCoords, nearbyFeatures, state.featureId, allowedLayerIds, (id) =>
            this.getFeature(id)
          );

          state.colocatedFeatures = colocated.length > 0 ? colocated : null;
        }
      }
    }
  },

  onMouseUp: function (state, event) {
    // Only validate and clear if we actually dragged
    // If dragMoving is false, we haven't started dragging yet (just set up from simple_select)
    if (state.dragMoving) {
      // Validate features after vertex move
      if (state.featureId) {
        const feature = this.getFeature(state.featureId);
        if (feature) {
          validateAfterMove.call(this, state, [feature.toGeoJSON() as Feature]);
        }
      }

      // @ts-expect-error - fireUpdate exists at runtime but not in types
      this.fireUpdate();

      // Clear colocated features and drag state after mouse up
      state.colocatedFeatures = null;
      state.dragMoveLocation = undefined;
      state.dragMoving = false;
    }

    CustomSnapDirectSelect.onMouseUp?.call(this, state, event);
  },

  onClick: function (state, event) {
    // If we're skipping this click (transitioned from vertex click in simple_select),
    // don't call the parent onClick which would deselect the feature
    if (state.skipNextClick) {
      state.skipNextClick = false;
      return;
    }

    // @ts-expect-error featureTarget is possibly undefined & needs to allow for that
    event.featureTarget = getFeatureTarget.call(this, event, this._ctx.options);
    CustomSnapDirectSelect.onClick?.call(this, state, event);
  },

  onDrag: function (state, event) {
    if (state.canDragMove !== true) return;
    state.dragMoving = true;
    // Get the position of the selected vertex before dragging for colocated features
    let beforeCoords: [number, number] | null = null;
    if (state.featureId && state.selectedCoordPaths && state.selectedCoordPaths.length === 1) {
      const feature = this.getFeature(state.featureId);
      if (feature) {
        beforeCoords = getVertexCoordinates(feature, state.selectedCoordPaths[0]);
      }
    }

    // Use our custom snap function that prioritizes Points over LineStrings
    const { lng, lat } = snapWithPointPriority(state, event);

    // Update the vertex position with the snapped coordinates
    // This is what CustomSnapDirectSelect.dragVertex does internally
    if (state.featureId && state.selectedCoordPaths && state.selectedCoordPaths.length === 1) {
      const feature = this.getFeature(state.featureId);
      if (feature) {
        const coordPath = state.selectedCoordPaths[0];
        feature.updateCoordinate(coordPath, lng, lat);
      }
    }

    // Calculate delta by comparing vertex position before and after
    let delta = { lng: 0, lat: 0 };
    if (beforeCoords && state.featureId && state.selectedCoordPaths && state.selectedCoordPaths.length === 1) {
      const feature = this.getFeature(state.featureId);
      if (feature) {
        const afterCoords = getVertexCoordinates(feature, state.selectedCoordPaths[0]);

        if (afterCoords) {
          delta = {
            lng: afterCoords[0] - beforeCoords[0],
            lat: afterCoords[1] - beforeCoords[1],
          };
        }
      }
    }

    // Move colocated features if topologicalEditing is enabled
    if (state.colocatedFeatures && state.colocatedFeatures.length > 0 && (delta.lng !== 0 || delta.lat !== 0)) {
      moveColocatedFeatures(state.colocatedFeatures, delta, (id) => this.getFeature(id));
    }
  },

  /**
   * By default direct select will not delete the selected feature, only selected vertices.
   * Here we override that behavior so that if no vertices are selected the entire feature
   * is deleted (subject to multi-layer editing restrictions).
   */
  onTrash: function (state) {
    if (!state.featureId) return;

    const feature = this.getFeature(state.featureId);
    if (!feature) return;

    const featuresToDelete = filterEditableFeatures(this._ctx.options, [feature.toGeoJSON() as Feature]);
    if (featuresToDelete.length === 0) return;

    // Feature is editable, proceed with deletion
    if (state.selectedCoordPaths && state.selectedCoordPaths.length > 0) {
      // Vertices are selected, allow vertex deletion
      CustomSnapDirectSelect.onTrash?.call(this, state);
    } else {
      // No vertices selected, delete the entire feature
      // deleteFeature automatically fires the delete event for history tracking
      this.deleteFeature(state.featureId);
      this.changeMode(Constants.modes.SIMPLE_SELECT, {});
    }
  },

  toDisplayFeatures: function (state, geojson: GeoJSON.Feature<GeoJSON.LineString>, display) {
    if (state.featureId === geojson.properties?.id) {
      if (geojson.properties) {
        geojson.properties.active = Constants.activeStates.ACTIVE;
      }
      display(geojson);

      createSupplementaryPoints(geojson, {
        midpoints: state.midPoints,
        selectedPaths: state.selectedCoordPaths,
      }).forEach(display);
    } else {
      if (geojson.properties) {
        geojson.properties.active = Constants.activeStates.INACTIVE;
      }
      display(geojson);
    }
    this.fireActionable?.(state);
  },
};

export default directSelect;
